import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Instagram, Facebook, MessageCircle, Send, CheckCircle2 } from "lucide-react";
import { api, fmtApiError } from "@/api";
import Reveal, { usePageMeta } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SUBJECTS = [
  "Voyageur — question sur un séjour",
  "Propriétaire — confier mon bien",
  "Partenariat ou autre demande",
];

export default function Contact() {
  usePageMeta("Contact", "Contactez Golden Aura Pro, conciergerie location courte durée à Oyonnax et Lyon. Propriétaires et voyageurs : réponse rapide et personnalisée.");
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", consent: false, website: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Votre demande a bien été envoyée.");
    } catch (err) {
      toast.error(fmtApiError(err));
    } finally {
      setSending(false);
    }
  };

  const email = settings?.contact_email || "goldenauraprof@gmail.com";
  const waNumber = (settings?.whatsapp || "").replace(/[^0-9]/g, "");

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="contact-page">
      <Reveal className="text-center mb-16">
        <p className="text-xs tracking-[0.35em] uppercase text-[#D4AF37] mb-5">Contact</p>
        <h1 className="font-display font-light text-4xl sm:text-5xl text-[#F6F1E6] mb-6">
          Écrivez-nous, <span className="italic gold-text">nous répondons vite.</span>
        </h1>
        <p className="text-base md:text-lg text-[#9AA3B5] max-w-2xl mx-auto">
          Vous êtes propriétaire et souhaitez maximiser vos revenus locatifs, ou voyageur en quête d'un séjour d'exception ? Notre équipe vous répond personnellement.
        </p>
      </Reveal>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Infos */}
        <Reveal className="lg:col-span-5">
          <div className="h-full rounded-3xl gold-frame bg-[#101A2E] p-8 sm:p-10 flex flex-col gap-7" data-testid="contact-info-panel">
            <p className="text-xs tracking-[0.3em] uppercase text-[#D4AF37]">Nos coordonnées</p>
            <a href={`mailto:${email}`} className="group flex items-center gap-4" data-testid="contact-email-link">
              <span className="w-11 h-11 rounded-full border border-[#D4AF37]/30 flex items-center justify-center shrink-0 group-hover:bg-[#D4AF37]/10 transition-colors"><Mail size={17} className="text-[#D4AF37]" /></span>
              <span>
                <span className="block text-[10px] tracking-[0.25em] uppercase text-[#6B7385]">E-mail</span>
                <span className="text-sm text-[#F6F1E6] group-hover:text-[#E5C158] transition-colors">{email}</span>
              </span>
            </a>
            {settings?.phone && (
              <a href={`tel:${settings.phone.replace(/\s/g, "")}`} className="group flex items-center gap-4" data-testid="contact-phone-link">
                <span className="w-11 h-11 rounded-full border border-[#D4AF37]/30 flex items-center justify-center shrink-0 group-hover:bg-[#D4AF37]/10 transition-colors"><Phone size={17} className="text-[#D4AF37]" /></span>
                <span>
                  <span className="block text-[10px] tracking-[0.25em] uppercase text-[#6B7385]">Téléphone</span>
                  <span className="text-sm text-[#F6F1E6] group-hover:text-[#E5C158] transition-colors">{settings.phone}</span>
                </span>
              </a>
            )}
            {waNumber && (
              <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-4" data-testid="contact-whatsapp-link">
                <span className="w-11 h-11 rounded-full border border-[#D4AF37]/30 flex items-center justify-center shrink-0 group-hover:bg-[#D4AF37]/10 transition-colors"><MessageCircle size={17} className="text-[#D4AF37]" /></span>
                <span>
                  <span className="block text-[10px] tracking-[0.25em] uppercase text-[#6B7385]">WhatsApp</span>
                  <span className="text-sm text-[#F6F1E6] group-hover:text-[#E5C158] transition-colors">Discuter sur WhatsApp</span>
                </span>
              </a>
            )}
            <a href={settings?.instagram_url || "https://www.instagram.com/golden_aura.conciergerie"} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-4" data-testid="contact-instagram-link">
              <span className="w-11 h-11 rounded-full border border-[#D4AF37]/30 flex items-center justify-center shrink-0 group-hover:bg-[#D4AF37]/10 transition-colors"><Instagram size={17} className="text-[#D4AF37]" /></span>
              <span>
                <span className="block text-[10px] tracking-[0.25em] uppercase text-[#6B7385]">Instagram</span>
                <span className="text-sm text-[#F6F1E6] group-hover:text-[#E5C158] transition-colors">@golden_aura.conciergerie</span>
              </span>
            </a>
            {settings?.facebook_url && (
              <a href={settings.facebook_url} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-4" data-testid="contact-facebook-link">
                <span className="w-11 h-11 rounded-full border border-[#D4AF37]/30 flex items-center justify-center shrink-0 group-hover:bg-[#D4AF37]/10 transition-colors"><Facebook size={17} className="text-[#D4AF37]" /></span>
                <span>
                  <span className="block text-[10px] tracking-[0.25em] uppercase text-[#6B7385]">Facebook</span>
                  <span className="text-sm text-[#F6F1E6] group-hover:text-[#E5C158] transition-colors">GOLDEN AURA PRO</span>
                </span>
              </a>
            )}
            <div className="flex items-center gap-4" data-testid="contact-zone">
              <span className="w-11 h-11 rounded-full border border-[#D4AF37]/30 flex items-center justify-center shrink-0"><MapPin size={17} className="text-[#D4AF37]" /></span>
              <span>
                <span className="block text-[10px] tracking-[0.25em] uppercase text-[#6B7385]">Zone d'intervention</span>
                <span className="text-sm text-[#F6F1E6]">{settings?.zone || "Oyonnax, Lyon et alentours"}</span>
              </span>
            </div>
          </div>
        </Reveal>

        {/* Form */}
        <Reveal delay={0.1} className="lg:col-span-7">
          <div className="rounded-3xl gold-frame bg-[#101A2E] p-8 sm:p-10">
            {sent ? (
              <div className="text-center py-14" data-testid="contact-success">
                <CheckCircle2 size={52} className="text-[#D4AF37] mx-auto mb-6" />
                <h2 className="font-display text-2xl sm:text-3xl text-[#F6F1E6] mb-4">Merci, votre demande est envoyée.</h2>
                <p className="text-sm text-[#9AA3B5] leading-relaxed max-w-md mx-auto">
                  Nous avons bien reçu votre message et vous répondrons dans les plus brefs délais à l'adresse indiquée.
                </p>
                <button onClick={() => { setSent(false); setForm({ name: "", email: "", phone: "", subject: "", message: "", consent: false, website: "" }); }} data-testid="contact-new-message-btn" className="outline-button rounded-full px-8 py-3 text-xs tracking-[0.16em] uppercase mt-8">
                  Envoyer un autre message
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
                <input
                  type="text"
                  name="website"
                  value={form.website}
                  onChange={(e) => set("website", e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute opacity-0 pointer-events-none h-0 w-0"
                  style={{ position: "absolute", left: "-9999px" }}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="c-name" className="text-xs tracking-[0.2em] uppercase text-[#9AA3B5]">Nom et prénom *</Label>
                    <Input id="c-name" required minLength={2} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="contact-name-input" className="mt-2 bg-[#0A0F1E] border-[#D4AF37]/20 focus:border-[#D4AF37] text-[#F6F1E6] h-12" placeholder="Jean Dupont" />
                  </div>
                  <div>
                    <Label htmlFor="c-email" className="text-xs tracking-[0.2em] uppercase text-[#9AA3B5]">Adresse e-mail *</Label>
                    <Input id="c-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email-input" className="mt-2 bg-[#0A0F1E] border-[#D4AF37]/20 focus:border-[#D4AF37] text-[#F6F1E6] h-12" placeholder="jean@exemple.fr" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="c-phone" className="text-xs tracking-[0.2em] uppercase text-[#9AA3B5]">Téléphone <span className="text-[#6B7385]">(facultatif)</span></Label>
                    <Input id="c-phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="contact-phone-input" className="mt-2 bg-[#0A0F1E] border-[#D4AF37]/20 focus:border-[#D4AF37] text-[#F6F1E6] h-12" placeholder="+33 6 12 34 56 78" />
                  </div>
                  <div>
                    <Label className="text-xs tracking-[0.2em] uppercase text-[#9AA3B5]">Objet de la demande *</Label>
                    <Select value={form.subject} onValueChange={(v) => set("subject", v)} required>
                      <SelectTrigger data-testid="contact-subject-select" className="mt-2 bg-[#0A0F1E] border-[#D4AF37]/20 text-[#F6F1E6] h-12">
                        <SelectValue placeholder="Choisissez un objet" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#101A2E] border-[#D4AF37]/30 text-[#F6F1E6]">
                        {SUBJECTS.map((s) => (
                          <SelectItem key={s} value={s} data-testid={`subject-option-${SUBJECTS.indexOf(s)}`}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="c-message" className="text-xs tracking-[0.2em] uppercase text-[#9AA3B5]">Votre message *</Label>
                  <Textarea id="c-message" required minLength={10} rows={6} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="contact-message-input" className="mt-2 bg-[#0A0F1E] border-[#D4AF37]/20 focus:border-[#D4AF37] text-[#F6F1E6]" placeholder="Décrivez votre demande…" />
                </div>
                <div className="flex items-start gap-3">
                  <Checkbox id="c-consent" checked={form.consent} onCheckedChange={(v) => set("consent", v === true)} data-testid="contact-consent-checkbox" className="mt-0.5 border-[#D4AF37]/40 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:text-[#0A0F1E]" />
                  <Label htmlFor="c-consent" className="text-xs text-[#9AA3B5] leading-relaxed cursor-pointer">
                    J'accepte que mes données soient traitées par Golden Aura Pro dans le cadre de ma demande, conformément à la{" "}
                    <Link to="/confidentialite" className="text-[#E5C158] underline underline-offset-2" data-testid="contact-privacy-link">politique de confidentialité</Link>. *
                  </Label>
                </div>
                <button
                  type="submit"
                  disabled={sending || !form.consent || !form.subject}
                  data-testid="contact-submit-btn"
                  className="gold-button w-full rounded-full px-8 py-4 text-xs font-medium tracking-[0.2em] uppercase flex items-center justify-center gap-3 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:transform-none"
                >
                  {sending ? "Envoi en cours…" : (<>Envoyer ma demande <Send size={14} /></>)}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </div>
  );
}
